import { CartLinesUpdateEvent } from '@shopify/events';
import { CartItemsComponent } from './component-cart-items.js';
class SlCartRecommendations extends HTMLElement {
  connectedCallback() {
    if(this.abort) return;
    this.abort=new AbortController();
    this.track=this.querySelector('[data-sl-track]');
    const on=(el,type,fn,options={})=>el?.addEventListener(type,fn,{...options,signal:this.abort.signal});
    this.previous=this.querySelector('[data-sl-prev]');
    this.next=this.querySelector('[data-sl-next]');
    const reduced=()=>matchMedia('(prefers-reduced-motion: reduce)').matches;
    const move=direction=>this.track.scrollBy({left:direction*this.track.clientWidth,behavior:reduced()?'instant':'smooth'});
    on(this.previous,'click',()=>move(-1)); on(this.next,'click',()=>move(1));
    const arrows=()=>{this.previous.disabled=this.track.scrollLeft<2;this.next.disabled=this.track.scrollLeft+this.track.clientWidth>=this.track.scrollWidth-2;};
    on(this.track,'scroll',arrows,{passive:true});
    this.resize=new ResizeObserver(arrows);this.resize.observe(this.track);arrows();
    this.mutations=new MutationObserver(()=>{if(this.track!==this.querySelector('[data-sl-track]')||this.previous!==this.querySelector('[data-sl-prev]')||this.next!==this.querySelector('[data-sl-next]')){this.disconnectedCallback();this.connectedCallback();}});
    this.mutations.observe(this,{childList:true,subtree:true});
    on(this.track,'keydown',e=>{if(e.target!==this.track)return;if(e.key==='ArrowRight'||e.key==='ArrowLeft'){e.preventDefault();move(e.key==='ArrowRight'?1:-1);}});
    let drag=null; let suppressClick=false;
    on(this.track,'pointerdown',e=>{if(e.pointerType!=='mouse'||e.button!==0||e.target.closest('button,input'))return;drag={x:e.clientX,left:this.track.scrollLeft,id:e.pointerId};suppressClick=false;});
    on(this.track,'pointermove',e=>{if(!drag)return;const dx=e.clientX-drag.x;if(Math.abs(dx)>5){suppressClick=true;this.track.classList.add('is-dragging');this.track.setPointerCapture(e.pointerId);this.track.scrollLeft=drag.left-dx;e.preventDefault();}});
    const stop=()=>{drag=null;this.track.classList.remove('is-dragging');};
    on(this.track,'pointerup',stop);on(this.track,'pointercancel',stop);on(this.track,'lostpointercapture',stop);
    on(this.track,'dragstart',e=>e.preventDefault());
    on(this.track,'click',e=>{if(suppressClick){e.preventDefault();e.stopImmediatePropagation();suppressClick=false;}},{capture:true});
    on(this,'click',e=>{const button=e.target.closest('[data-sl-cart-add] button');if(button){e.preventDefault();this.add(button.closest('form'));}},{capture:true});
    on(this,'submit',e=>{const form=e.target.closest('[data-sl-cart-add]');if(form){e.preventDefault();this.add(form);}});
  }
  disconnectedCallback(){this.abort?.abort();this.abort=null;this.resize?.disconnect();this.mutations?.disconnect();}
  async add(form) {
    if(this.busy)return;this.busy=true;
    const button=form.querySelector('button');const error=this.querySelector('[data-sl-error]');
    error.hidden=true;button.disabled=true;button.setAttribute('aria-busy','true');
    let deferred;
    let added=false;
    try {
      await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);
      const body=new FormData(form);
      const root=window.Shopify?.routes?.root||'/';
      const ids=[...new Set([...document.querySelectorAll('cart-items-component')].map(el=>el.dataset.sectionId).filter(Boolean))].slice(0,5);
      body.set('sections',ids.join(','));body.set('sections_url',location.pathname);
      deferred=CartLinesUpdateEvent.createPromise();deferred.promise.catch(()=>{});
      document.dispatchEvent(new CartLinesUpdateEvent({action:'add',context:'cart',lines:[{merchandiseId:String(body.get('id')),quantity:Number(body.get('quantity'))||1}],promise:deferred.promise}));
      const response=await fetch(root+'cart/add.js',{method:'POST',credentials:'same-origin',headers:{Accept:'application/json'},body});
      const result=await response.json();
      if(!response.ok||result.status)throw new Error(result.description||this.dataset.errorText);
      added=true;
      const cartResponse=await fetch(root+'cart.js',{credentials:'same-origin',headers:{Accept:'application/json'}});
      if(!cartResponse.ok)throw new Error(this.dataset.refreshText);
      const cart=await cartResponse.json();
      deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{items:cart.items,source:'sl-cart-recommendations',productId:form.dataset.productId,itemCount:Number(body.get('quantity'))||1,sections:result.sections,didError:false}});
      const status=this.querySelector('[data-sl-status]');if(status)status.textContent=this.dataset.addedText;
    }catch(caught){
      deferred?.reject(caught);
      if(this.isConnected){error.textContent=added?this.dataset.refreshText:(caught instanceof TypeError||caught instanceof SyntaxError?this.dataset.networkText:caught.message||this.dataset.errorText);error.hidden=false;}
    }finally{this.busy=false;button.disabled=false;button.removeAttribute('aria-busy');}
  }
}
if(!customElements.get('sl-cart-recommendations'))customElements.define('sl-cart-recommendations',SlCartRecommendations);

function slDeliveryDate(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Copenhagen', year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', hourCycle:'h23' }).formatToParts(now);
  const number = type => Number(parts.find(part => part.type === type).value);
  const date = new Date(Date.UTC(number('year'), number('month')-1, number('day') + (number('hour') < 12 ? 1 : 2)));
  return new Intl.DateTimeFormat((document.documentElement.lang || '').toLowerCase().startsWith('sv') ? 'sv-SE' : 'da-DK', { day:'numeric', month:'short', timeZone:'UTC' }).format(date);
}
class SlCartDeliveryDate extends HTMLElement {
  connectedCallback() {
    this.updateDate = () => { const value = slDeliveryDate(); if (this.textContent !== value) this.textContent = value; };
    this.updateDate();
    this.timer = setInterval(this.updateDate, 30000);
    document.addEventListener('visibilitychange', this.updateDate);
    this.observer = new MutationObserver(() => { if (!this.textContent.trim()) this.updateDate(); });
    this.observer.observe(this, { childList: true, characterData: true, subtree: true });
  }
  disconnectedCallback() {
    clearInterval(this.timer);
    document.removeEventListener('visibilitychange', this.updateDate);
    this.observer?.disconnect();
  }
}
if (!customElements.get('sl-cart-delivery-date')) customElements.define('sl-cart-delivery-date', SlCartDeliveryDate);
